import { type KeyboardEvent, type RefObject, useEffect, useLayoutEffect, useRef } from 'react';
import { LinienSymbol } from '../ui/linien-symbol.js';
import pille from './symbol-pille.module.css';

export const BLOCK_KOMMENTIEREN = 'Block kommentieren';
export const BLOCK_ENTFERNEN = 'Block entfernen';

export interface BlockPilleProps {
  blockIndex: number;
  /** Die Textflaeche, in der Block und Leiste stehen; Bezug fuer die gemessene Lage. */
  wurzel: RefObject<HTMLElement | null>;
  kommentieren: boolean;
  entfernen: boolean;
  onKommentieren(blockIndex: number): void;
  onEntfernen(blockIndex: number): void;
}

// Vorauswahl moeglicher Tab-Stopps; was davon wirklich erreichbar ist, entscheidet `erreichbar`.
const TABSTOPPS = 'a[href], button, input, textarea, select, [tabindex]';

/**
 * Erreicht Tab das Element wirklich? Der Selektor allein reicht nicht (Codex-Review PR #112,
 * MITTEL): Ein Link mit `tabindex="-1"` passt auf `a[href]`, und Nachfahren ausgeblendeter
 * Bloecke (`data-dopplung`, `display: none`) passen auf alles. Sichtbarkeit wird ueber
 * Attribute und berechnete Stile geprueft, nicht ueber das Layout: jsdom liefert keine
 * Rechtecke, und auch im Browser hat ein Element mit leerem Inhalt keine.
 */
function erreichbar(element: HTMLElement): boolean {
  if (element.tabIndex < 0 || element.matches(':disabled')) return false;
  if (element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
  const stil = getComputedStyle(element);
  if (stil.visibility === 'hidden' || stil.visibility === 'collapse') return false;
  for (let knoten: Element | null = element; knoten; knoten = knoten.parentElement) {
    if (getComputedStyle(knoten).display === 'none') return false;
  }
  return true;
}

function tabstoppKandidaten(wurzel: Element): HTMLElement[] {
  return [...wurzel.querySelectorAll<HTMLElement>(TABSTOPPS)].filter(erreichbar);
}

export function blockElement(wurzel: HTMLElement | null, blockIndex: number): HTMLElement | null {
  const element = wurzel?.querySelector(`[data-block-index="${blockIndex}"]`);
  return element instanceof HTMLElement ? element : null;
}

/**
 * Die Tab-Stopps eines Blocks in Dokumentreihenfolge: er selbst (wenn fokussierbar), dann seine
 * Nachfahren, etwa Links im Text. Die Leiste folgt erst nach dem letzten davon (Codex-Review
 * PR #112, HOCH: sonst waeren Links im Ratgebertext per Tastatur unerreichbar). Ein offener Block
 * ist ein Eingabefeld: Tab verlaesst ihn direkt, seine Links sind dort keine Tab-Stopps.
 */
export function tabstoppsIm(block: HTMLElement): HTMLElement[] {
  // Derselbe Filter wie fuer die Nachfahren (Codex-Review PR #115, MITTEL): ein ausgeblendeter
  // oder inerter Block ist selbst kein Tab-Stopp.
  const selbst = erreichbar(block) ? [block] : [];
  if (block.getAttribute('contenteditable') === 'true') return selbst;
  return [...selbst, ...tabstoppKandidaten(block)];
}

/** Der erste Tab-Stopp hinter dem Block, ausserhalb jeder Block-Leiste, oder null. */
function naechsterTabstopp(wurzel: HTMLElement, block: HTMLElement): HTMLElement | null {
  for (const kandidat of tabstoppKandidaten(wurzel)) {
    if (kandidat.closest('[data-block-pille]') || block.contains(kandidat)) continue;
    if (block.compareDocumentPosition(kandidat) & Node.DOCUMENT_POSITION_FOLLOWING) {
      return kandidat;
    }
  }
  return null;
}

/** Der letzte Tab-Stopp vor dem Block, ausserhalb jeder Block-Leiste, oder null. */
export function vorigerTabstopp(wurzel: HTMLElement, block: HTMLElement): HTMLElement | null {
  let vorher: HTMLElement | null = null;
  for (const kandidat of tabstoppKandidaten(wurzel)) {
    if (kandidat.closest('[data-block-pille]') || block.contains(kandidat)) continue;
    if (!(block.compareDocumentPosition(kandidat) & Node.DOCUMENT_POSITION_PRECEDING)) break;
    vorher = kandidat;
  }
  return vorher;
}

/**
 * Lage schwebend ueber der oberen rechten Ecke des Blocks, ausserhalb seiner Box wie in Google
 * Docs: Die Unterkante der Leiste liegt auf der Oberkante des Blocks, ihre linke Kante an seiner
 * rechten. Rechts neben den Bloecken steht in der Spalte kein Text, die Leiste verdeckt also
 * weder den eigenen noch den vorigen Block. Nur wenn die Flaeche dafuer zu schmal ist, rueckt
 * sie nach links an den Rand. Gemessene Laufzeitkoordinaten, keine Gestaltungswerte.
 */
function messe(element: HTMLElement, wurzel: HTMLElement, block: HTMLElement): void {
  const b = block.getBoundingClientRect();
  const w = wurzel.getBoundingClientRect();
  // Ohne Layout (jsdom) liefert der Browser nur Nullen: dann bleibt es beim Stil (oben links).
  if (b.width === 0 && b.height === 0) return;
  const oben = b.top - w.top - element.offsetHeight;
  const links = Math.min(b.right - w.left, w.width - element.offsetWidth);
  element.style.top = `${Math.max(0, oben)}px`;
  element.style.left = `${Math.max(0, links)}px`;
}

/**
 * Symbolleiste an einem Block (Karte j3gkZH2Hgc6K57Oa8f1F): Kommentieren und Entfernen ohne
 * vorheriges Markieren, auch waehrend der Block bearbeitet wird. Steht im DOM am Ende der
 * Textflaeche, nicht neben dem Block: Ein Element zwischen zwei Bloecken braeche die
 * Nachbarschaftsregeln in `website-text.module.css` (etwa Pro/Contra, `.block + .block`). Die
 * Tab-Reihenfolge Block, seine Links, Leiste, naechster Block stellen deshalb `text-flaeche.tsx`
 * (vom letzten Tab-Stopp des Blocks hierher und rueckwaerts) und diese Komponente (von hier
 * hinter den Block und zurueck) her.
 */
export function BlockPille({
  blockIndex,
  wurzel,
  kommentieren,
  entfernen,
  onKommentieren,
  onEntfernen,
}: BlockPilleProps) {
  const element = useRef<HTMLDivElement>(null);

  // Nach jedem Render vor dem Malen: Der Block kann beim Tippen wachsen oder nach dem Schliessen
  // neu umbrechen. Dazu Fenstergroesse und spaet geladene Bilder (veraendern die Hoehe der
  // Flaeche), die keinen Render ausloesen.
  const neuMessen = () => {
    const el = element.current;
    const flaeche = wurzel.current;
    const block = blockElement(flaeche, blockIndex);
    if (el && flaeche && block) messe(el, flaeche, block);
  };
  const neuMessenRef = useRef(neuMessen);
  useLayoutEffect(() => {
    neuMessenRef.current = neuMessen;
    neuMessen();
  });
  useEffect(() => {
    const neuMessen = () => neuMessenRef.current();
    // Steht die Leiste schon beim ersten Render (offener Block beim Einhaengen), haengt der Ref der
    // Flaeche im Layout-Effekt oben noch nicht: Eltern setzen ihre Refs nach den Kindern.
    neuMessen();
    window.addEventListener('resize', neuMessen);
    const flaeche = wurzel.current;
    const beobachter =
      typeof ResizeObserver === 'function' && flaeche ? new ResizeObserver(neuMessen) : null;
    if (flaeche) beobachter?.observe(flaeche);
    return () => {
      window.removeEventListener('resize', neuMessen);
      beobachter?.disconnect();
    };
  }, [wurzel, blockIndex]);

  function taste(ereignis: KeyboardEvent<HTMLDivElement>) {
    if (ereignis.key !== 'Tab' || ereignis.altKey || ereignis.ctrlKey || ereignis.metaKey) return;
    const flaeche = wurzel.current;
    const block = blockElement(flaeche, blockIndex);
    const knoepfe = Array.from(
      element.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? [],
    );
    if (!flaeche || !block || knoepfe.length === 0) return;
    // Rueckwaerts vom ersten Symbol auf den letzten Tab-Stopp des Blocks, als stuende die Leiste
    // direkt dahinter.
    if (ereignis.shiftKey) {
      const letzter = tabstoppsIm(block).at(-1);
      if (ereignis.target === knoepfe[0] && letzter) {
        ereignis.preventDefault();
        letzter.focus();
      }
      return;
    }
    // Vorwaerts vom letzten Symbol hinter den Block. Gibt es dort keinen Tab-Stopp mehr, fuehrt
    // der Browser ohnehin richtig weiter: Die Leiste steht am Ende der Flaeche.
    if (ereignis.target !== knoepfe[knoepfe.length - 1]) return;
    const weiter = naechsterTabstopp(flaeche, block);
    if (weiter) {
      ereignis.preventDefault();
      weiter.focus();
    }
  }

  if (!kommentieren && !entfernen) return null;
  return (
    <div
      ref={element}
      className={`${pille.pille} ${pille.amBlock}`}
      role="toolbar"
      aria-label="Block"
      data-block-pille={blockIndex}
      onKeyDown={taste}
    >
      {kommentieren && (
        <button
          type="button"
          className={pille.knopf}
          aria-label={BLOCK_KOMMENTIEREN}
          title={BLOCK_KOMMENTIEREN}
          onClick={() => onKommentieren(blockIndex)}
        >
          <LinienSymbol name="message" />
        </button>
      )}
      {entfernen && (
        <button
          type="button"
          className={pille.knopf}
          aria-label={BLOCK_ENTFERNEN}
          title={BLOCK_ENTFERNEN}
          onClick={() => onEntfernen(blockIndex)}
        >
          <LinienSymbol name="trash" />
        </button>
      )}
    </div>
  );
}
